import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mutate } from 'swr';
import container from 'src/container';
import { AdminPermission, CreateAdminPayload } from 'src/domain/admin';
import Input from 'src/components/FormComponents/Input';
import PasswordInput from 'src/components/FormComponents/PasswordInput';
import { Button } from 'src/components/Buttons/Button';
import { DesktopIcon, EnvelopeIcon } from 'src/components/Icons/icons';
import {
    IsString,
    IsNotEmpty,
    IsEmail,
    MinLength,
    IsBoolean,
} from 'class-validator';
import Form from 'src/components/FormComponents/Form';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { useNotification } from 'src/components/Notification/useNotfication';
import { checkIfEmailExist } from 'src/domain/user';
import i18n from 'src/i18n';

const {
    cradle: { munduClient },
} = container;
class CreateAdminSchema {
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    firstName: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    lastName: string;

    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @IsEmail({}, { message: () => i18n.t('validation.invalidEmail') })
    email: string;

    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    @MinLength(6, { message: () => i18n.t('validation.passwordTooShort') })
    initialPassword: string;

    @IsBoolean()
    support = false;
}

export const CreateAdminForm = () => {
    const notify = useNotification();
    const { t } = useTranslation();
    const history = useHistory();

    const onSubmit = async (data: CreateAdminSchema): Promise<void> => {
        const payload: CreateAdminPayload = {
            email: data.email,
            profile: {
                firstName: data.firstName,
                lastName: data.lastName,
            },
            initialPassword: data.initialPassword,
            adminPermissions: data.support ? [AdminPermission.support] : [],
        };

        try {
            await checkIfEmailExist(data.email);
            await munduClient.createAdmin(payload);
            notify(t('admin.notifications.adminCreated'));
            mutate('listAdmins');
            history.push('/app/admin');
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        }
    };

    return (
        <Form
            schema={CreateAdminSchema}
            className="w-full h-full"
            mode="onChange"
            onSubmit={onSubmit}
        >
            {({ register, formState: { errors } }) => (
                <>
                    <div className="col-span-full flex justify-center">
                        {/* TODO: Replace with Role icon when ready */}
                        <div className="rounded-full w-36 h-36 bg-starkWhite flex justify-center items-center mb-4 text-[18px] stroke-2 ">
                            <TextIcon SVG={DesktopIcon} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Input
                            type="text"
                            className="text-input"
                            label={t('admin.forms.createAdmin.firstName')}
                            error={errors.firstName}
                            {...register('firstName')}
                        />
                        <Input
                            type="text"
                            className="text-input"
                            label={t('admin.forms.createAdmin.lastName')}
                            error={errors.lastName}
                            {...register('lastName')}
                        />

                        <Input
                            type="email"
                            autoComplete="off"
                            className="text-input"
                            leadingIcon={EnvelopeIcon}
                            label={t('admin.forms.createAdmin.email')}
                            error={errors.email}
                            {...register('email')}
                        />
                        <PasswordInput
                            autoComplete="new-password"
                            className="text-input"
                            label={t('admin.forms.createAdmin.initialPassword')}
                            error={errors.initialPassword}
                            {...register('initialPassword')}
                        />
                        <label className="col-span-full inline-flex items-center gap-3 py-2 cursor-pointer">
                            <input type="checkbox" {...register('support')} />
                            <span>
                                {t('admin.forms.createAdmin.supportPermission')}
                            </span>
                        </label>
                    </div>
                    <div className="inline-flex justify-end py-6 items-end w-full">
                        <Button
                            submit
                            title={t('admin.forms.createAdmin.submit')}
                        />
                    </div>
                </>
            )}
        </Form>
    );
};
